import { useState } from "react";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useCancelarRascunho } from "@/hooks/use-projeto-rascunho";
import { useToast } from "@/hooks/use-toast";
import { formatApiError } from "@/lib/api-error";

interface CancelarRascunhoButtonProps {
  projetoId: string;
  freelancerNome: string | null | undefined;
  temVaga: boolean;
  className?: string;
}

export function CancelarRascunhoButton({ projetoId, freelancerNome, temVaga, className }: CancelarRascunhoButtonProps) {
  const [open, setOpen] = useState(false);
  const cancelar = useCancelarRascunho();
  const { toast } = useToast();

  const confirmar = async () => {
    try {
      await cancelar.mutateAsync(projetoId);
      setOpen(false);
      toast({
        title: "Projeto cancelado",
        description: temVaga ? "Para retomar, use \"Voltar para rascunho\" no card da vaga." : undefined,
      });
    } catch (error) {
      const formatted = formatApiError(error);
      toast({ variant: "destructive", title: formatted.title, description: formatted.detail });
    }
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className={className}
        data-testid="projeto-cancelar"
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
      >
        Cancelar
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent onClick={(event) => event.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar o projeto{freelancerNome ? ` de ${freelancerNome}` : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              O rascunho sai da lista de projetos.
              {temVaga && " A vaga continua em Aprovado, e o card mostra o projeto cancelado com a opção de voltar para rascunho."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelar.isPending}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              disabled={cancelar.isPending}
              onClick={(event) => {
                event.preventDefault();
                void confirmar();
              }}
              className="gap-1.5"
            >
              {cancelar.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Cancelar projeto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
